/**
 * The API client. Every call carries a Firebase ID token; the server derives
 * the uid from it and never from anything we send.
 */
import { boot } from './boot'
import type {
  CellarQuestion,
  CellarWine,
  CriticReview,
  FamilyResponse,
  MenuQuestion,
  ProgressEvent,
  ScanCreated,
  ScanDocument,
  SignedTarget,
  WineDetail,
} from './types'

async function authHeader(): Promise<Record<string, string>> {
  const { auth } = await boot()
  const user = auth.currentUser
  if (!user) throw new Error('Not signed in.')
  return { Authorization: `Bearer ${await user.getIdToken()}` }
}

// FastAPI puts the human-readable half in `detail` for a handler's own
// HTTPException, but a 422 from request validation instead puts a list of
// `{loc, msg, type}` objects there — stringifying that array directly
// produces "[object Object]", so pull the messages out by hand.
function errorDetail(body: unknown, fallback: string): string {
  const detail = (body as { detail?: unknown } | null)?.detail
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    const messages = detail
      .map((entry) => (entry && typeof entry === 'object' ? (entry as { msg?: unknown }).msg : null))
      .filter((msg): msg is string => typeof msg === 'string')
    if (messages.length) return messages.join('; ')
  }
  return fallback
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(await authHeader()),
      ...init.headers,
    },
  })
  if (!response.ok) {
    const body = await response.json().catch(() => null)
    throw new Error(errorDetail(body, `${response.status} ${response.statusText}`))
  }
  return response.status === 204 ? (undefined as T) : ((await response.json()) as T)
}

export const api = {
  personas: () =>
    request<{ defaultId: string; personas: Persona[] }>('/api/personas'),

  createScan: (body: {
    images: { imageId: string; contentType: string; seenAt: string | null }[]
    personaId: string
    researchDepth: string
    /** `cellar` (the default) or `shopping`. */
    mode?: string
    /** What the shopping is for. Ignored outside shopping mode. */
    context?: string
  }) =>
    request<ScanCreated>('/api/scans', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  startScan: (runId: string, imageIds: string[]) =>
    request<{ runId: string; imageCount: number }>(`/api/scans/${runId}/start`, {
      method: 'POST',
      body: JSON.stringify({ imageIds }),
    }),

  getScan: (runId: string) => request<ScanDocument>(`/api/scans/${runId}`),

  listScans: () => request<{ runs: ScanDocument[] }>('/api/scans'),

  cellar: () => request<{ wines: CellarWine[] }>('/api/cellar'),

  setInventory: (wineId: string, count: number) =>
    request<{ wineId: string; bottleCount: number }>(
      `/api/cellar/${wineId}/inventory`,
      { method: 'PUT', body: JSON.stringify({ count }) },
    ),

  /** `wineIds` are the bottles the question is about, not the ones it may be
   *  answered with — a finished bottle is a perfectly good subject. */
  askCellar: (question: string, wineIds: string[] = []) =>
    request<CellarQuestion>('/api/cellar/questions', {
      method: 'POST',
      body: JSON.stringify({ question, wineIds }),
    }),

  cellarQuestions: () =>
    request<{ questions: CellarQuestion[] }>('/api/cellar/questions'),

  createMenu: (images: { imageId: string; contentType: string }[]) =>
    request<{
      menuId: string
      uploads: { imageId: string; target: SignedTarget }[]
    }>('/api/menu', { method: 'POST', body: JSON.stringify({ images }) }),

  askMenu: (
    menuId: string,
    body: { wineImageIds: string[]; foodImageIds: string[]; notes: string },
  ) =>
    request<MenuQuestion>(`/api/menu/${menuId}/ask`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  menuQuestions: () => request<{ questions: MenuQuestion[] }>('/api/menu/questions'),

  wine: (wineId: string) => request<WineDetail>(`/api/wines/${wineId}`),

  rate: (wineId: string, stars: number) =>
    request<{ average: number; count: number; myStars: number }>(
      `/api/wines/${wineId}/rating`,
      { method: 'PUT', body: JSON.stringify({ stars }) },
    ),

  comment: (wineId: string, text: string, displayName: string) =>
    request<Comment>(`/api/wines/${wineId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ text, displayName }),
    }),

  deleteComment: (wineId: string, commentId: string) =>
    request<{ deleted: string }>(`/api/wines/${wineId}/comments/${commentId}`, {
      method: 'DELETE',
    }),

  requestCriticReview: (wineId: string, personaId: string, notes?: string) =>
    request<CriticReview>(`/api/wines/${wineId}/reviews`, {
      method: 'POST',
      body: JSON.stringify({ personaId, notes: notes ?? '' }),
    }),

  family: () => request<FamilyResponse>('/api/family'),

  createFamily: () => request<FamilyResponse>('/api/family', { method: 'POST' }),

  joinFamily: (inviteCode: string) =>
    request<FamilyResponse>('/api/family/join', {
      method: 'POST',
      body: JSON.stringify({ inviteCode }),
    }),

  leaveFamily: () => request<FamilyResponse>('/api/family/leave', { method: 'POST' }),

  regenerateInviteCode: () =>
    request<{ inviteCode: string }>('/api/family/invite-code', { method: 'POST' }),

  voices: () => request<{ defaultId: string; voices: VoiceItem[] }>('/api/voices'),

  fetchTtsBlob: async (text: string, voiceId?: string, runId?: string): Promise<Blob> => {
    const headers = await authHeader()
    const response = await fetch('/api/tts', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      body: JSON.stringify({ text, voiceId, runId }),
    })
    if (!response.ok) {
      const body = await response.json().catch(() => null)
      throw new Error(errorDetail(body, `Speech synthesis failed (${response.status})`))
    }
    return response.blob()
  },
}

export interface Persona {
  id: string
  name: string
  tagline: string
  sampleLine: string
}

export interface VoiceItem {
  id: string
  name: string
  voiceId: string
  modelId?: string
  languageCode?: string
}

export interface Comment {
  commentId: string
  userId: string
  displayName: string
  text: string
  createdAt: string
}

/**
 * Upload one blob straight to GCS with a signed PUT URL.
 *
 * Every header named in the URL's `X-Goog-SignedHeaders` has to be sent back
 * exactly as it was signed. There are two: `Content-Type`, and the
 * `x-goog-content-length-range` that caps the upload size. Omitting either is
 * a 400 whose body explains itself but which the browser will not let you
 * read, so the message below carries whatever GCS said if it is legible.
 */
export async function uploadSigned(
  target: SignedTarget,
  blob: Blob,
): Promise<void> {
  const response = await fetch(target.url, {
    method: 'PUT',
    headers: {
      'Content-Type': target.contentType,
      'x-goog-content-length-range': target['x-goog-content-length-range'],
    },
    body: blob,
  })
  if (!response.ok) {
    const detail = await response.text().catch(() => '')
    const reason = /<Details>([^<]+)<\/Details>/.exec(detail)?.[1]
    throw new Error(
      reason
        ? `Upload failed (${response.status}): ${reason}`
        : `Upload failed (${response.status}).`,
    )
  }
}

/**
 * Server-sent events over `fetch`, because `EventSource` cannot send an
 * Authorization header and this API has no cookies.
 *
 * Yields parsed events until the stream ends or `signal` aborts. Losing the
 * connection is not an error worth surfacing: the Firestore listener behind
 * this is the channel that is allowed to be authoritative.
 */
export async function* streamScan(
  runId: string,
  signal: AbortSignal,
): AsyncGenerator<ProgressEvent> {
  const response = await fetch(`/api/scans/${runId}/stream`, {
    headers: await authHeader(),
    signal,
  })
  if (!response.ok || !response.body) throw new Error(`Stream failed (${response.status}).`)

  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader()
  let buffer = ''
  while (true) {
    const { done, value } = await reader.read()
    if (done) return
    buffer += value
    // SSE frames are separated by a blank line; a chunk boundary can land
    // anywhere, so hold the tail until the next read completes it.
    const frames = buffer.split('\n\n')
    buffer = frames.pop() ?? ''
    for (const frame of frames) {
      const data = frame
        .split('\n')
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.slice(5).trim())
        .join('')
      if (!data) continue
      try {
        yield JSON.parse(data) as ProgressEvent
      } catch {
        // A truncated frame is not worth taking the screen down for.
      }
    }
  }
}

/** Turn a `gs://` URI into something an `<img>` can load. */
export function mediaUrl(uri: string | null | undefined): string | undefined {
  return uri ? `/api/media?uri=${encodeURIComponent(uri)}` : undefined
}
